"use client"

import { useEffect, useState } from "react"
import { Info } from "lucide-react"
import { useTranslations } from "next-intl"
import { Button } from "@/shared/components/ui/button"
import { captureClientEvent } from "@/shared/lib/monitoring/capture-client"
import {
  reloadStaleDeploymentManually,
  isStaleChunkLoadError,
  isStaleDeploymentRecoveryBlocked,
  markStaleDeploymentRecoveryBlocked,
  performStaleDeploymentReload,
} from "@/shared/lib/stale-deployment"

export function StaleDeploymentRecoveryListener() {
  const t = useTranslations("errors.staleDeployment")
  const [showRefreshPrompt, setShowRefreshPrompt] = useState(false)

  useEffect(() => {
    if (isStaleDeploymentRecoveryBlocked()) setShowRefreshPrompt(true)

    function recover(error: unknown) {
      if (!isStaleChunkLoadError(error)) return
      if (performStaleDeploymentReload() !== "blocked") return

      markStaleDeploymentRecoveryBlocked()
      setShowRefreshPrompt(true)
      captureClientEvent("stale_deployment_recovery", {
        outcome: "blocked",
        scope: "stale_chunk_recovery",
      })
    }

    function handleError(event: ErrorEvent) {
      recover(event.error)
    }
    function handleRejection(event: PromiseRejectionEvent) {
      recover(event.reason)
    }

    window.addEventListener("error", handleError)
    window.addEventListener("unhandledrejection", handleRejection)
    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleRejection)
    }
  }, [])

  if (!showRefreshPrompt) return null

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed left-1/2 top-4 z-[100] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-[var(--radius)] border border-border bg-card px-4 py-3"
    >
      <Info className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-medium text-card-foreground">{t("title")}</p>
        <p className="text-sm text-muted-foreground">{t("description")}</p>
      </div>
      <Button
        size="sm"
        onClick={reloadStaleDeploymentManually}
      >
        {t("action")}
      </Button>
    </div>
  )
}
